import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { Box, Collapse, Link, Paper, Stack, TextField, Typography } from "@mui/material";
import ClientDropdown from "./Dropdowns/ClientDropdown";

const clientToDropdownData = (client) =>
  client
    ? {
        clientId: client.id,
        name: client.name,
        email: client.email,
        passportNumber: client.passportNumber,
      }
    : null;

/**
 * Client field: search existing clients, or expand to enter walk-in contact details.
 */
const ClientPickerWithManualEntry = ({
  existingValue = null,
  onExistingChange,
  manualClient = { name: "", email: "", phone: "" },
  onManualChange,
  useManual = false,
  onUseManualChange,
  disabled = false,
  locked = false,
  lockedDisplayName = "",
  autocompleteLabel = "Select client",
  onExistingSelectAttempt,
}) => {
  if (locked) {
    return (
      <TextField
        size="small"
        fullWidth
        label={autocompleteLabel}
        value={lockedDisplayName || existingValue?.name || ""}
        disabled
      />
    );
  }

  const openManual = () => {
    onUseManualChange?.(true);
    onExistingChange?.(null);
  };

  const closeManual = () => {
    onUseManualChange?.(false);
    onManualChange?.({ name: "", email: "", phone: "" });
  };

  const setManualField = (field) => (e) => {
    onManualChange?.({ ...manualClient, [field]: e.target.value });
  };

  return (
    <Box>
      {/* unmountOnExit: the inactive branch holds a `required` autocomplete input.
          Collapse only hides (height 0) by default, leaving that empty required
          control in the DOM — native validation then fails on a not-focusable
          control and silently blocks the form submit. */}
      <Collapse in={!useManual} unmountOnExit>
        <ClientDropdown
          data={clientToDropdownData(existingValue)}
          onChange={onExistingChange}
          onSelectAttempt={onExistingSelectAttempt}
          disabled={disabled}
          hideEditButton
        />
        {!disabled && (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={openManual}
            sx={{
              mt: 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontWeight: 500,
            }}
          >
            <PersonAddOutlinedIcon sx={{ fontSize: 16 }} />
            Client not in the system? Enter their details
          </Link>
        )}
      </Collapse>

      <Collapse in={useManual} unmountOnExit>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: (theme) => (theme.palette.mode === "dark" ? "action.hover" : "grey.50"),
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Walk-in client
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Contact details for this booking only — not added to your client list.
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              label="Full name *"
              size="small"
              fullWidth
              value={manualClient.name}
              onChange={setManualField("name")}
              disabled={disabled}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Email *"
                type="email"
                size="small"
                fullWidth
                value={manualClient.email}
                onChange={setManualField("email")}
                disabled={disabled}
              />
              <TextField
                label="Phone"
                size="small"
                fullWidth
                value={manualClient.phone}
                onChange={setManualField("phone")}
                disabled={disabled}
              />
            </Stack>
          </Stack>
        </Paper>
        {!disabled && (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={closeManual}
            sx={{
              mt: 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontWeight: 500,
            }}
          >
            <SearchOutlinedIcon sx={{ fontSize: 16 }} />
            Search existing clients instead
          </Link>
        )}
      </Collapse>
    </Box>
  );
};

export default ClientPickerWithManualEntry;
